// ImportacionFormXL - Sprint 44 Fase 3 Vista 10.
// Form contenedor multi-modalidad. Header + grid 3:1 con TipoPills + tabs +
// ResumenSidebar. 6 tabs (basicos/tracking/costos/lineas/documentos/auditoria).
import { useEffect, useMemo } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import useImportacionFormXL from './hooks/useImportacionFormXL';
import { TABS, TAB_LABELS, showTracking, showCostos } from './utils';
import TipoPillsXL from './components/TipoPillsXL';
import ResumenSidebarXL from './components/ResumenSidebarXL';
import BasicosTabXL from './tabs/BasicosTabXL';
import TrackingTabXL from './tabs/TrackingTabXL';
import CostosTabXL from './tabs/CostosTabXL';
import AuditoriaTabXL from './tabs/AuditoriaTabXL';
import LineasPanelXL from './LineasPanelXL';
import DocumentosPanelXL from './DocumentosPanelXL';
import SendButtons from '../../components/shared/SendButtons';
import XLChip from '../../components/xl/XLChip';
import XLSkeleton from '../../components/xl/XLSkeleton';

const CSS_ID = 'xl-imp-form-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-hdr { display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.xl-hdr__left { display: flex; align-items: center; gap: 14px; min-width: 0 }
.xl-hdr__back {
  font: 500 13px/1 var(--xl-font-sans);
  color: var(--xl-color-text-secondary);
  background: transparent; border: none; cursor: pointer;
  padding: 6px 8px; border-radius: var(--xl-radius-md);
}
.xl-hdr__back:hover { background: var(--xl-color-surface-alt); color: var(--xl-color-text) }
.xl-hdr__title { margin: 0; font-size: 20px; font-weight: 600; line-height: 1.2 }
.xl-hdr__sub { margin: 2px 0 0; font-size: 12px; color: var(--xl-color-text-secondary) }
.xl-hdr__actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap }
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
}
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse) }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-btn--ghost {
  background: transparent; color: var(--xl-color-text-secondary);
  border-color: var(--xl-color-border);
}
.xl-btn--ghost:hover { background: var(--xl-color-surface); color: var(--xl-color-text) }

.xl-banner { padding: 10px 14px; margin-bottom: 12px; border-radius: var(--xl-radius-md); font-size: 13px }
.xl-banner--ok  { background: rgba(5,150,105,0.10); color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-banner--err { background: rgba(220,38,38,0.10); color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }

.xl-imp-grid {
  display: grid; grid-template-columns: 3fr 1fr; gap: 16px; align-items: flex-start;
}
@media (max-width: 1100px) { .xl-imp-grid { grid-template-columns: 1fr } }
.xl-imp-col { display: flex; flex-direction: column; gap: 16px }

.xl-card {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  overflow: hidden;
}
.xl-tabs {
  display: flex; gap: 6px; flex-wrap: wrap;
  padding: 12px 16px;
  border-bottom: 1px solid var(--xl-color-border);
  background: var(--xl-color-bg);
}
.xl-body { padding: 20px }
.xl-loading { padding: 20px; display: flex; flex-direction: column; gap: 10px }
`;

export default function ImportacionFormXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const fm = useImportacionFormXL();
  const t = fm.form.tipo_importacion;

  const tabsVisibles = useMemo(() => TABS.filter((k) => {
    if (k === 'tracking' && !showTracking(t)) return false;
    if (k === 'costos' && !showCostos(t)) return false;
    return true;
  }), [t]);

  useEffect(() => {
    if (!tabsVisibles.includes(fm.tab)) fm.setTab('basicos');
  }, [tabsVisibles, fm]);

  if (!fm.loaded) {
    return (
      <div className="xl-page">
        <div className="xl-loading" aria-busy="true">
          <XLSkeleton variant="line" width="40%" height={20} />
          <XLSkeleton variant="rect" height={120} />
          <XLSkeleton variant="rect" height={220} />
        </div>
      </div>
    );
  }

  const titulo = fm.isNew ? 'Nueva importacion' : (fm.form.referencia || 'Importacion');
  const subtitulo = fm.isNew ? 'Crear importacion multi-modalidad' : 'Editar';
  const docId = fm.form.id || (fm.isNew ? null : Number(fm.id));

  return (
    <div className="xl-page">
      <header className="xl-hdr">
        <div className="xl-hdr__left">
          <button type="button" className="xl-hdr__back"
            onClick={() => fm.navigate('/importaciones')}>{'\u2190 Volver'}</button>
          <div>
            <h2 className="xl-hdr__title">{titulo}</h2>
            <p className="xl-hdr__sub">{subtitulo}</p>
          </div>
        </div>
        <div className="xl-hdr__actions">
          {!fm.isNew && (
            <>
              <SendButtons
                endpointBase={'/importaciones/' + fm.id}
                tipoLabel="importacion"
                defaultEmail={fm.form.proveedor_email || ''}
                defaultTelefono={fm.form.proveedor_telefono || ''} />
              <button type="button" className="xl-btn xl-btn--ghost"
                onClick={fm.onPdf}>PDF</button>
            </>
          )}
          <button type="button" className="xl-btn xl-btn--primary"
            onClick={fm.onSave} disabled={fm.saving}>
            {fm.saving ? 'Guardando\u2026' : (fm.isNew ? 'Crear' : 'Guardar')}
          </button>
        </div>
      </header>

      {fm.msg && (
        <div className={'xl-banner ' + (fm.msg.kind === 'ok' ? 'xl-banner--ok' : 'xl-banner--err')}
          role={fm.msg.kind === 'ok' ? 'status' : 'alert'}
          onClick={fm.clearMsg}>
          {fm.msg.text}
        </div>
      )}

      <div className="xl-imp-grid">
        <div className="xl-imp-col">
          <TipoPillsXL value={t} onChange={(v) => fm.set('tipo_importacion', v)} />

          <div className="xl-card">
            <div className="xl-tabs" role="tablist">
              {tabsVisibles.map((k) => (
                <XLChip key={k} onClick={() => fm.setTab(k)} selected={fm.tab === k}>
                  {TAB_LABELS[k]}
                </XLChip>
              ))}
            </div>
            <div className="xl-body">
              {fm.tab === 'basicos' && (
                <BasicosTabXL form={fm.form} set={fm.set} />
              )}
              {fm.tab === 'tracking' && showTracking(t) && (
                <TrackingTabXL
                  form={fm.form} set={fm.set}
                  couriers={fm.couriers}
                  onVerTracking={fm.onVerTracking}
                  courierSeleccionado={fm.courierSeleccionado}
                  isNew={fm.isNew} />
              )}
              {fm.tab === 'costos' && showCostos(t) && (
                <CostosTabXL form={fm.form} set={fm.set}
                  onTrmToday={fm.onTrmToday}
                  onTrmViernes={fm.onTrmViernes}
                  showTrm tipo={t} />
              )}
              {fm.tab === 'lineas' && (
                <LineasPanelXL importacionId={docId} isNew={fm.isNew}
                  onDistribuir={fm.onDistribuir} />
              )}
              {fm.tab === 'documentos' && (
                <DocumentosPanelXL importacionId={docId} isNew={fm.isNew} />
              )}
              {fm.tab === 'auditoria' && <AuditoriaTabXL form={fm.form} />}
            </div>
          </div>
        </div>

        <ResumenSidebarXL form={fm.form} courierSeleccionado={fm.courierSeleccionado} />
      </div>
    </div>
  );
}
